import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  CheckCircle2,
  Clock,
  X,
  Send,
  Trash2,
  Copy,
  ExternalLink,
  Loader2,
  AlertCircle,
  ListOrdered
} from 'lucide-react';

const MultiSignature = ({ contractId, contractTitle, onUpdate }) => {
  const { api } = useAuth();
  const [signers, setSigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [newSigner, setNewSigner] = useState({ email: '', name: '' });
  const [pendingSigners, setPendingSigners] = useState([]);
  const [requireOrder, setRequireOrder] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchSigners();
  }, [contractId]);

  const fetchSigners = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/signers`);
      setSigners(response.data.signers || []);
    } catch (error) {
      console.error('Error fetching signers:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPendingSigner = () => {
    if (!newSigner.email || !newSigner.name) {
      toast.error('Completa el nombre y email del firmante');
      return;
    }
    
    if (pendingSigners.some(s => s.email === newSigner.email)) {
      toast.error('Este email ya está en la lista');
      return;
    }

    setPendingSigners([...pendingSigners, { 
      ...newSigner, 
      order: pendingSigners.length + signers.length + 1 
    }]);
    setNewSigner({ email: '', name: '' });
  };

  const removePendingSigner = (email) => {
    setPendingSigners(pendingSigners.filter(s => s.email !== email));
  };

  const sendInvitations = async () => {
    if (pendingSigners.length === 0) {
      toast.error('Agrega al menos un firmante');
      return;
    }

    setInviting(true);
    try {
      const response = await api.post(`/contracts/${contractId}/signers`, {
        signers: pendingSigners,
        require_order: requireOrder
      });
      
      toast.success(`${response.data.signers.length} firmantes invitados`);
      setPendingSigners([]);
      setShowInviteForm(false);
      fetchSigners();
      onUpdate?.();
    } catch (error) {
      console.error('Error inviting signers:', error);
      toast.error('Error al enviar invitaciones');
    } finally {
      setInviting(false);
    }
  };

  const removeSigner = async (signerId) => {
    try {
      await api.delete(`/contracts/${contractId}/signers/${signerId}`);
      toast.success('Firmante eliminado');
      fetchSigners();
      onUpdate?.();
    } catch (error) {
      console.error('Error removing signer:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar firmante');
    }
  };

  const copySigningLink = (signer) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/sign/${contractId}/${signer.signing_token || 'invalid'}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado al portapapeles');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'signed':
        return (
          <span className="flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            Firmado
          </span>
        );
      case 'pending':
        return (
          <span className="flex items-center gap-1 text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            Pendiente
          </span>
        );
      case 'declined':
        return (
          <span className="flex items-center gap-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">
            <X className="w-3 h-3" />
            Rechazado
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-400">Cargando firmantes...</span>
        </div>
      </div>
    );
  }

  const signedCount = signers.filter(s => s.status === 'signed').length;
  const totalSigners = signers.length;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-gradient-to-r from-purple-500/5 to-blue-500/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            <h3 className="font-semibold">Firma Múltiple</h3>
            {totalSigners > 0 && (
              <span className="text-xs bg-slate-700 px-2 py-0.5 rounded-full">
                {signedCount}/{totalSigners} firmados
              </span>
            )}
          </div>
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            data-testid="invite-signers-btn"
            className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
          >
            <UserPlus className="w-4 h-4" />
            Invitar Firmantes
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <div className="p-4 border-b border-border bg-slate-800/30">
          <div className="space-y-4">
            {/* Add Signer Form */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Nombre del firmante"
                value={newSigner.name}
                onChange={(e) => setNewSigner({ ...newSigner, name: e.target.value })}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                data-testid="signer-name-input"
              />
              <input
                type="email"
                placeholder="Email"
                value={newSigner.email}
                onChange={(e) => setNewSigner({ ...newSigner, email: e.target.value })}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                data-testid="signer-email-input"
              />
              <button
                onClick={addPendingSigner}
                data-testid="add-signer-btn"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-medium transition-colors"
              >
                Agregar
              </button>
            </div>

            {/* Pending Signers List */}
            {pendingSigners.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Firmantes a invitar:</span>
                  <label className="flex items-center gap-2 text-sm text-slate-400">
                    <input
                      type="checkbox"
                      checked={requireOrder}
                      onChange={(e) => setRequireOrder(e.target.checked)}
                      className="rounded bg-slate-700 border-slate-600"
                    />
                    <ListOrdered className="w-4 h-4" />
                    Firma secuencial
                  </label>
                </div>
                
                {pendingSigners.map((signer, index) => (
                  <div 
                    key={signer.email}
                    className="flex items-center justify-between p-2 bg-slate-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {requireOrder && (
                        <span className="w-6 h-6 flex items-center justify-center bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">
                          {index + 1}
                        </span>
                      )}
                      <div>
                        <div className="text-sm font-medium">{signer.name}</div>
                        <div className="text-xs text-slate-400">{signer.email}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => removePendingSigner(signer.email)}
                      className="p-1 text-slate-400 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  onClick={sendInvitations}
                  disabled={inviting}
                  data-testid="send-invitations-btn"
                  className="w-full py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  {inviting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Enviar {pendingSigners.length} Invitación{pendingSigners.length !== 1 ? 'es' : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signers List */}
      <div className="p-4">
        {signers.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay firmantes invitados</p>
            <p className="text-xs mt-1">Invita a otras personas a firmar este documento</p>
          </div>
        ) : (
          <div className="space-y-2">
            {signers.map((signer) => (
              <div 
                key={signer.id}
                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                data-testid={`signer-${signer.id}`}
              >
                <div className="flex items-center gap-3">
                  {signer.order && (
                    <span className="w-6 h-6 flex items-center justify-center bg-slate-700 text-slate-300 rounded-full text-xs font-medium">
                      {signer.order}
                    </span>
                  )}
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {signer.name}
                      {getStatusBadge(signer.status)}
                    </div>
                    <div className="text-xs text-slate-400 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {signer.email}
                    </div>
                    {signer.signed_at && (
                      <div className="text-xs text-slate-500 mt-1">
                        Firmado: {new Date(signer.signed_at).toLocaleDateString('es-CL')}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {signer.status === 'pending' && (
                    <>
                      <button
                        onClick={() => copySigningLink(signer)}
                        title="Copiar enlace de firma"
                        className="p-2 text-slate-400 hover:text-blue-400 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeSigner(signer.id)}
                        title="Eliminar firmante"
                        className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress Bar */}
        {totalSigners > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>Progreso de firmas</span>
              <span>{Math.round((signedCount / totalSigners) * 100)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-500"
                style={{ width: `${(signedCount / totalSigners) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSignature;
